import capitalize from 'lodash/capitalize'
import * as Yup from 'yup'
import {
  CategoryOption,
  FeederSubtype,
  FeederType,
  NamedRecord,
  OrderProvider,
  ProductRow,
  SaveOutcome,
  SelectOption,
  ServerErrors,
  WeightUnit,
  toSelectOption
} from '../catalogueTypes'
import { categoryOptionValue } from './categoryFormValues'
import { t } from '../../../i18n'

const scope = 'product'

export interface ProductFormValues {
  name: string
  location: SelectOption | null
  attachment: File | null
  file_url: string | null
  fraction_id: SelectOption | null
  unit_id: SelectOption | null
  feeder_dosing_mode_id: SelectOption | null
  sauce_dosing_mode_id: SelectOption | null
  alergen_ids: SelectOption[]
  dosing_weight_step: number | string
  alert: number | string
  kcal: number | string
  kjoul: number | string
  notes: string
  processing_loss: number | string
  volume_to_weight_multiplier: number | string
  feeder_type: FeederType | ''
  feeder_subtype: FeederSubtype | null
  max_container_amount: number | string
  dosing_time: number | string | null
  simphony_external_id: string
  infor_external_id: string
  is_modifier: boolean | null
}

const DEFAULT_MAX_CONTAINER_AMOUNT: Record<WeightUnit, number> = { g: 2000, oz: 70.5 }

const SERVER_TO_FORM_FIELD: Record<string, string> = { product_category: 'location' }

const toOption = (record: NamedRecord | null): SelectOption | null => (record ? toSelectOption(record) : null)

export const emptyProductValues = (weightUnit: WeightUnit): ProductFormValues => ({
  name: '',
  location: null,
  attachment: null,
  file_url: null,
  fraction_id: null,
  unit_id: null,
  feeder_dosing_mode_id: null,
  sauce_dosing_mode_id: null,
  alergen_ids: [],
  dosing_weight_step: 1,
  alert: 0,
  kcal: 0,
  kjoul: 0,
  notes: '',
  processing_loss: 0,
  volume_to_weight_multiplier: 0,
  feeder_type: '',
  feeder_subtype: null,
  max_container_amount: DEFAULT_MAX_CONTAINER_AMOUNT[weightUnit],
  dosing_time: null,
  simphony_external_id: '',
  infor_external_id: '',
  is_modifier: null
})

export const productFormValues = (product: ProductRow, categoryOptions: CategoryOption[]): ProductFormValues => ({
  name: product.name,
  location: categoryOptionValue(categoryOptions, product.product_category_id),
  attachment: null,
  file_url: product.file_url,
  fraction_id: toOption(product.fraction),
  unit_id: toOption(product.unit),
  feeder_dosing_mode_id: toOption(product.feeder_dosing_mode),
  sauce_dosing_mode_id: toOption(product.sauce_dosing_mode),
  alergen_ids: product.alergens.map(toSelectOption),
  dosing_weight_step: product.dosing_weight_step,
  alert: product.alert,
  kcal: product.kcal,
  kjoul: product.kjoul,
  notes: product.notes ?? '',
  processing_loss: product.processing_loss,
  volume_to_weight_multiplier: product.volume_to_weight_multiplier,
  feeder_type: product.feeder_type ?? '',
  feeder_subtype: product.feeder_subtype,
  max_container_amount: product.max_container_amount,
  dosing_time: product.dosing_time,
  simphony_external_id: product.simphony_external_id ?? '',
  infor_external_id: product.infor_external_id ?? '',
  is_modifier: product.is_modifier
})

export const productValidationSchema = Yup.object({
  location: Yup.mixed()
    .nullable()
    .required(t('validations.location.required', { scope })),
  feeder_dosing_mode_id: Yup.mixed()
    .nullable()
    .when('feeder_type', {
      is: (feederType: FeederType | '') => feederType !== 'liquid',
      then: (schema) => schema.required(t('validations.feeder_dosing_mode.required', { scope }))
    }),
  sauce_dosing_mode_id: Yup.mixed()
    .nullable()
    .when('feeder_type', {
      is: 'liquid',
      then: (schema) => schema.required(t('validations.sauce_dosing_mode.required', { scope }))
    }),
  dosing_weight_step: Yup.number()
    .nullable()
    .required(t('validations.dosing_weight_step.required', { scope }))
    .moreThan(0, t('validations.dosing_weight_step.greater_than', { scope })),
  is_modifier: Yup.boolean()
    .nullable()
    .required(t('validations.is_modifier.required', { scope }))
})

type Scalar = string | number | boolean | null

const appendScalar = (form: FormData, key: string, value: Scalar) => form.append(key, value === null ? '' : String(value))

const optionValue = (option: SelectOption | null): number | null => option?.value ?? null

export const toProductFormData = (values: ProductFormValues): FormData => {
  const form = new FormData()
  appendScalar(form, 'name', values.name)
  if (values.attachment) form.append('attachment', values.attachment)
  appendScalar(form, 'fraction_id', optionValue(values.fraction_id))
  appendScalar(form, 'unit_id', optionValue(values.unit_id))
  appendScalar(form, 'feeder_dosing_mode_id', optionValue(values.feeder_dosing_mode_id))
  appendScalar(form, 'sauce_dosing_mode_id', optionValue(values.sauce_dosing_mode_id))
  appendScalar(form, 'alergen_ids', values.alergen_ids.map((option) => option.value).join(','))
  appendScalar(form, 'dosing_weight_step', values.dosing_weight_step)
  appendScalar(form, 'alert', values.alert)
  appendScalar(form, 'kcal', values.kcal)
  appendScalar(form, 'kjoul', values.kjoul)
  appendScalar(form, 'notes', values.notes)
  appendScalar(form, 'processing_loss', values.processing_loss)
  appendScalar(form, 'volume_to_weight_multiplier', values.volume_to_weight_multiplier)
  appendScalar(form, 'feeder_type', values.feeder_type)
  appendScalar(form, 'feeder_subtype', values.feeder_subtype)
  appendScalar(form, 'max_container_amount', values.max_container_amount)
  appendScalar(form, 'dosing_time', values.dosing_time)
  appendScalar(form, 'simphony_external_id', values.simphony_external_id)
  appendScalar(form, 'infor_external_id', values.infor_external_id)
  appendScalar(form, 'is_modifier', values.is_modifier)
  if (values.location) appendScalar(form, 'product_category_id', values.location.value)
  return form
}

export const toProductFormErrors = (errors: ServerErrors): ServerErrors =>
  Object.fromEntries(Object.entries(errors).map(([field, messages]) => [SERVER_TO_FORM_FIELD[field] ?? field, messages]))

type SaveProduct = (form: FormData, productId: number | null, viewedCategoryId: number | null) => Promise<SaveOutcome>

export const saveProductOutcome = (
  save: SaveProduct,
  values: ProductFormValues,
  productId: number | null,
  viewedCategoryId: number | null
): Promise<SaveOutcome> =>
  save(toProductFormData(values), productId, viewedCategoryId).then((outcome) =>
    outcome.ok ? outcome : { ok: false, errors: toProductFormErrors(outcome.errors) }
  )

export const orderProviderFieldName = (provider: OrderProvider): 'infor_external_id' | 'simphony_external_id' | string =>
  `${provider.provider_type.toLowerCase()}_external_id`

export const orderProviderLabel = (provider: OrderProvider): string =>
  t('catalogue.external_id', { provider: capitalize(provider.provider_type) })

export const orderProviderExternalId = (product: ProductRow, provider: OrderProvider): string => {
  const fieldName = orderProviderFieldName(provider)
  return fieldName === 'infor_external_id' || fieldName === 'simphony_external_id' ? product[fieldName] ?? '' : ''
}
